import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { api } from "@/lib/api";
import { HighlightedText } from "@/components/HighlightedText";
import { useAuth } from "@/context/AuthContext";
import { storage } from "@/utils/storage";
import { TOKEN_KEY } from "@/lib/api";

/**
 * Symbiosis Room — WebSocket-backed shared chat with a live third-party AI
 * called "Third". Users can be anonymous by picking a name on join.
 */
export default function Room() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [text, setText] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [joined, setJoined] = useState(!!user?.name);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.getRoom(shareId);
      setRoom(r);
      setMessages(r?.messages || []);
    } catch (e) {
      setError(e?.message || "Room not found");
    }
  }, [shareId]);

  useEffect(() => {
    load();
  }, [load]);

  const connect = useCallback(async () => {
    if (!name.trim() || wsRef.current) return;
    // Attach token via query param since browser WS API can't set headers
    const token = await storage.secureGet(TOKEN_KEY, "");
    const wsUrl = api.roomWebSocketUrl(shareId, name.trim()) + (token ? `&token=${encodeURIComponent(token)}` : "");
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
    };
    ws.onerror = () => setError("Live connection error");
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "message" || msg.type === "third") {
          setMessages((prev) => [...prev, msg]);
        } else if (msg.type === "history") {
          setMessages(msg.messages || []);
        } else if (msg.type === "deleted") {
          setMessages([]);
        }
      } catch {
        /* ignore */
      }
    };
    setJoined(true);
  }, [name, shareId]);

  useEffect(() => {
    return () => {
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = () => {
    const content = text.trim();
    if (!content) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", content }));
    setText("");
  };

  const askThird = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "ask_third" }));
  };

  const shareUrl = api.roomShareUrl(shareId);

  return (
    <div data-testid="room-screen" style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          borderBottom: `1px solid ${COLORS.border}`,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => navigate("/symbiosis")}
          data-testid="room-back"
          style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "transparent", border: "none", padding: 0, cursor: "pointer",
            color: "var(--fg-55)", fontFamily: FONTS.body, fontSize: 11,
            letterSpacing: "0.24em", textTransform: "uppercase",
          }}
        >
          <span style={{
            width: 14, height: 14, borderRadius: "50%",
            background: "radial-gradient(34% 30% at 32% 26%, #fff, #8AFFC4 22%, #05060c 100%)",
            boxShadow: "0 0 12px rgba(138,255,196,0.8)",
          }} />
          <span>symbiosis</span>
        </button>
        <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 18 }}>
          {room?.title || "Symbiosis room"}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: connected ? COLORS.symbiosis : COLORS.textMuted,
              display: "inline-block",
            }}
          />
          <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11 }}>
            {connected ? "live" : "offline"}
          </span>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(shareUrl);
              alert("Room link copied");
            }}
            data-testid="room-copy-link"
            style={{ marginLeft: 8, color: COLORS.symbiosis, fontFamily: FONTS.body, fontSize: 12 }}
          >
            Copy link
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, color: COLORS.error, fontFamily: FONTS.body, fontSize: 14 }}>
          {error}
        </div>
      )}

      {!joined ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ maxWidth: 380, width: "100%", textAlign: "center" }}>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 24, marginBottom: 12 }}>
              Join room
            </div>
            <div style={{ color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 14, marginBottom: 20 }}>
              Enter a name so others can see who's speaking. YUO's Third can join the conversation on request.
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && connect()}
              placeholder="Your name"
              autoFocus
              data-testid="room-join-name"
              style={{
                width: "100%",
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                padding: 14,
                color: COLORS.textPrimary,
                fontFamily: FONTS.body,
                fontSize: 15,
                outline: "none",
                marginBottom: 12,
              }}
            />
            <button
              onClick={connect}
              disabled={!name.trim()}
              data-testid="room-join-btn"
              style={{
                width: "100%",
                background: COLORS.symbiosis,
                color: "#000",
                borderRadius: 999,
                padding: "12px",
                fontFamily: FONTS.body,
                fontSize: 14,
                fontWeight: 700,
                opacity: name.trim() ? 1 : 0.5,
              }}
            >
              Join
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {messages.map((m, i) => {
              const isThird = m.type === "third" || m.role === "third";
              const mine = m.name === name.trim();
              return (
                <div
                  key={m.message_id || i}
                  style={{
                    display: "flex",
                    justifyContent: isThird ? "flex-start" : mine ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "85%",
                      background: isThird ? "transparent" : COLORS.surface,
                      border: isThird ? "none" : `1px solid ${COLORS.border}`,
                      borderLeft: isThird ? `2px solid ${COLORS.symbiosis}` : undefined,
                      padding: "10px 14px",
                      borderRadius: isThird ? 0 : 18,
                      color: COLORS.textPrimary,
                      fontFamily: FONTS.body,
                      fontSize: 15,
                    }}
                  >
                    <div style={{ color: isThird ? COLORS.symbiosis : COLORS.textMuted, fontSize: 10, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                      {isThird ? "Third" : m.name || "Guest"}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {isThird ? <HighlightedText content={m.content} /> : m.content}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={listEndRef} />
          </div>
          <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: 12, display: "flex", gap: 8, alignItems: "flex-end" }}>
            <button
              onClick={askThird}
              data-testid="room-ask-third"
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                border: `1px solid rgba(var(--role-green-rgb),0.40)`,
                background: `rgba(var(--role-green-rgb),0.09)`,
                color: COLORS.symbiosis,
                fontFamily: FONTS.body,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Ask Third
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Speak here…"
              rows={1}
              data-testid="room-input"
              style={{
                flex: 1,
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 20,
                padding: "10px 14px",
                color: COLORS.textPrimary,
                fontFamily: FONTS.body,
                fontSize: 15,
                outline: "none",
                minHeight: 44,
                maxHeight: 120,
              }}
            />
            <button
              onClick={send}
              disabled={!text.trim() || !connected}
              data-testid="room-send"
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                background: COLORS.symbiosis,
                color: "#000",
                fontSize: 18,
                fontWeight: 700,
                opacity: !text.trim() || !connected ? 0.4 : 1,
              }}
            >
             
            </button>
          </div>
        </>
      )}
    </div>
  );
}
